import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    Stethoscope, LayoutDashboard, Briefcase, User, Star, Bell, LogOut, 
    Menu, X, DollarSign, CheckCircle, Clock, AlertTriangle, FileText,
    MapPin, Calendar, Loader2, Phone, Mail, XCircle, ChevronRight,
    TrendingUp, Wallet, CreditCard, Receipt, ArrowUpRight, Settings,
    BookOpen, Brain, Users, Search, Send, Copy, ExternalLink, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getProviderAxios = () => {
    const token = localStorage.getItem('provider_token');
    return axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` }
    });
};

export default function MediProviderDashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [provider, setProvider] = useState(null);
    const [loading, setLoading] = useState(true);
    const [jobs, setJobs] = useState({ available_jobs: [], my_jobs: [] });
    const [notifications, setNotifications] = useState([]);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [respondingTo, setRespondingTo] = useState(null);
    const [earnings, setEarnings] = useState(null);
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [subscribing, setSubscribing] = useState(false);
    
    // Clinical Tools State
    const [clinicalLoading, setClinicalLoading] = useState(false);
    const [diagnosticInput, setDiagnosticInput] = useState({
        symptoms: '',
        history: '',
        vitals: '',
        examFindings: '',
        patientAge: '',
        patientSex: ''
    });
    const [diagnosticResult, setDiagnosticResult] = useState(null);
    const [guidelineSearch, setGuidelineSearch] = useState('');
    const [guidelines, setGuidelines] = useState([]);
    const [secondOpinionForm, setSecondOpinionForm] = useState({
        case_summary: '',
        question: '',
        specialty_needed: ''
    });
    const [secondOpinions, setSecondOpinions] = useState([]);
    const [communityQuestions, setCommunityQuestions] = useState([]);

    useEffect(() => {
        const providerData = localStorage.getItem('provider_user');
        if (!providerData) {
            navigate('/mediprovider/login');
            return;
        }
        setProvider(JSON.parse(providerData));
        fetchData();
    }, [navigate]);

    const fetchData = async () => {
        try {
            const [profileRes, jobsRes, notifRes, earningsRes] = await Promise.all([
                getProviderAxios().get('/mediprovider/me'),
                getProviderAxios().get('/provider/job-requests'),
                getProviderAxios().get('/notifications'),
                getProviderAxios().get('/mediprovider/earnings').catch(() => ({ data: null }))
            ]);
            setProvider(profileRes.data);
            setJobs(jobsRes.data);
            setNotifications(notifRes.data);
            if (earningsRes.data) setEarnings(earningsRes.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem('provider_token');
                localStorage.removeItem('provider_user');
                navigate('/mediprovider/login');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchSubscriptionPlans = async () => {
        try {
            const res = await getProviderAxios().get('/mediprovider/subscription/plans');
            setSubscriptionPlans(res.data.plans || []);
        } catch (err) {
            console.error('Failed to fetch subscription plans');
        }
    };

    const handleSubscribe = async (planId) => {
        setSubscribing(true);
        try {
            const res = await getProviderAxios().post('/mediprovider/subscribe', { plan: planId });
            if (res.data.checkout_url) {
                window.location.href = res.data.checkout_url;
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to start subscription');
        } finally {
            setSubscribing(false);
        }
    };

    const updatePayoutSchedule = async (schedule) => {
        try {
            await getProviderAxios().put(`/mediprovider/payout-schedule?schedule=${schedule}`);
            toast.success(`Payout schedule updated to ${schedule}`);
            fetchData();
        } catch (err) {
            toast.error('Failed to update payout schedule');
        }
    };

    // Clinical Tools Functions
    const runDiagnosticAid = async () => {
        if (!diagnosticInput.symptoms.trim()) {
            toast.error('Please enter patient symptoms');
            return;
        }
        setClinicalLoading(true);
        try {
            const res = await getProviderAxios().post('/clinical/diagnostic-aid', diagnosticInput);
            setDiagnosticResult(res.data);
            toast.success('Diagnostic analysis complete');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to generate assessment aid');
        } finally {
            setClinicalLoading(false);
        }
    };

    const searchGuidelines = async () => {
        if (!guidelineSearch.trim()) return;
        setClinicalLoading(true);
        try {
            const res = await getProviderAxios().get(`/clinical/guidelines?query=${encodeURIComponent(guidelineSearch)}`);
            setGuidelines(res.data.guidelines || []);
        } catch (err) {
            toast.error('Failed to search guidelines');
        } finally {
            setClinicalLoading(false);
        }
    };

    const submitSecondOpinion = async () => {
        if (!secondOpinionForm.case_summary.trim() || !secondOpinionForm.question.trim()) {
            toast.error('Please fill in case summary and question');
            return;
        }
        setClinicalLoading(true);
        try {
            await getProviderAxios().post('/clinical/second-opinion', secondOpinionForm);
            toast.success('Second opinion request submitted');
            setSecondOpinionForm({ case_summary: '', question: '', specialty_needed: '' });
            fetchCommunityQuestions();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to submit request');
        } finally {
            setClinicalLoading(false);
        }
    };

    const fetchCommunityQuestions = async () => {
        try {
            const res = await getProviderAxios().get('/clinical/community-questions');
            setCommunityQuestions(res.data || []);
        } catch (err) {
            console.error('Failed to fetch community questions');
        }
    };

    const submitAnswer = async (questionId, answer) => {
        try {
            await getProviderAxios().post(`/clinical/community-questions/${questionId}/answer`, { answer });
            toast.success('Answer submitted');
            fetchCommunityQuestions();
        } catch (err) {
            toast.error('Failed to submit answer');
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('provider_token');
        localStorage.removeItem('provider_user');
        navigate('/mediprovider/login');
    };

    const handleAcceptJob = async (jobId) => {
        setRespondingTo(jobId);
        try {
            const response = await getProviderAxios().post(`/provider/job-requests/${jobId}/respond`, {
                action: 'accept',
                message: 'I can help with this request'
            });
            toast.success(`Job accepted! Your earnings: $${response.data.your_earnings}`);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to accept job');
        } finally {
            setRespondingTo(null);
        }
    };

    const handleDeclineJob = async (jobId) => {
        setRespondingTo(jobId);
        try {
            await getProviderAxios().post(`/provider/job-requests/${jobId}/respond`, {
                action: 'decline'
            });
            toast.success('Job declined');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to decline job');
        } finally {
            setRespondingTo(null);
        }
    };

    const handleCompleteJob = async (jobId) => {
        setRespondingTo(jobId);
        try {
            const response = await getProviderAxios().post(`/provider/job-requests/${jobId}/complete`);
            toast.success(`Job completed! Earnings: $${response.data.earnings}`);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to complete job');
        } finally {
            setRespondingTo(null);
        }
    };

    const getFolderStatusColor = (status) => {
        switch (status) {
            case 'green': return 'bg-green-500';
            case 'amber': return 'bg-amber-500';
            case 'red': return 'bg-red-500';
            default: return 'bg-slate-500';
        }
    };

    const getUrgencyBadge = (urgency) => {
        switch (urgency) {
            case 'emergency': return 'bg-red-500/20 text-red-500';
            case 'urgent': return 'bg-amber-500/20 text-amber-500';
            default: return 'bg-blue-500/20 text-blue-500';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-slate-900 dark:to-slate-800">
            {/* Mobile Header */}
            <div className="lg:hidden flex items-center justify-between p-4 bg-white dark:bg-slate-800 border-b">
                <div className="flex items-center gap-3">
                    <Stethoscope className="w-8 h-8 text-teal-500" />
                    <span className="font-bold">Provider Portal</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
                    {sidebarOpen ? <X /> : <Menu />}
                </Button>
            </div>

            <div className="flex">
                {/* Sidebar */}
                <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-slate-800 border-r transform transition-transform lg:transform-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                    <div className="p-6 hidden lg:block border-b">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                                <Stethoscope className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="font-bold text-lg">Provider Portal</h1>
                                <p className="text-xs text-muted-foreground">MediGuide</p>
                            </div>
                        </div>
                    </div>

                    {/* Folder Status */}
                    <div className="p-4 border-b">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Folder Status</span>
                            <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${getFolderStatusColor(provider?.folder_status)}`} />
                                <span className="text-sm font-medium capitalize">{provider?.folder_status}</span>
                            </div>
                        </div>
                        {provider?.folder_status === 'red' && (
                            <p className="text-xs text-red-500 mt-2">⚠️ Update documents to receive jobs</p>
                        )}
                        {provider?.folder_status === 'amber' && (
                            <p className="text-xs text-amber-500 mt-2">⚠️ Documents expiring soon</p>
                        )}
                    </div>

                    <nav className="p-4 space-y-2">
                        {[
                            { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
                            { id: 'jobs', label: 'Job Requests', icon: Briefcase },
                            { id: 'clinical', label: 'Clinical Tools', icon: Brain },
                            { id: 'earnings', label: 'Earnings', icon: Wallet },
                            { id: 'profile', label: 'My Profile', icon: User },
                            { id: 'notifications', label: 'Notifications', icon: Bell },
                        ].map((item) => {
                            const Icon = item.icon;
                            const isActive = activeTab === item.id;
                            const notifCount = item.id === 'jobs' ? jobs.available_jobs.length : 
                                              item.id === 'notifications' ? notifications.filter(n => !n.read).length : 0;
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => { 
                                        setActiveTab(item.id); 
                                        setSidebarOpen(false);
                                        if (item.id === 'earnings') fetchSubscriptionPlans();
                                        if (item.id === 'clinical') fetchCommunityQuestions();
                                    }}
                                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                                        isActive
                                            ? 'bg-teal-500 text-white'
                                            : 'text-muted-foreground hover:bg-teal-50 dark:hover:bg-slate-700'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <Icon className="w-5 h-5" />
                                        {item.label}
                                    </div>
                                    {notifCount > 0 && (
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isActive ? 'bg-white text-teal-500' : 'bg-teal-500 text-white'}`}>
                                            {notifCount}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </nav>

                    <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-teal-500 flex items-center justify-center text-white font-bold">
                                {provider?.name?.charAt(0) || 'P'}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{provider?.name}</p>
                                <p className="text-xs text-muted-foreground capitalize">{provider?.profession}</p>
                            </div>
                        </div>
                        <Button 
                            variant="ghost" 
                            className="w-full justify-start text-muted-foreground hover:text-foreground"
                            onClick={handleLogout}
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </Button>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="flex-1 p-4 lg:p-8 overflow-auto min-h-screen">
                    {/* Dashboard Tab */}
                    {activeTab === 'dashboard' && (
                        <>
                            <div className="mb-6">
                                <h1 className="text-2xl font-bold mb-1">Welcome, {provider?.name?.split(' ')[0]}!</h1>
                                <p className="text-muted-foreground">Your daily overview</p>
                            </div>

                            {/* Stats */}
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-muted-foreground">Available Jobs</p>
                                                <p className="text-2xl font-bold text-teal-500">{jobs.available_jobs.length}</p>
                                            </div>
                                            <Briefcase className="w-8 h-8 text-teal-500/30" />
                                        </div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-muted-foreground">Jobs Completed</p>
                                                <p className="text-2xl font-bold">{provider?.jobs_completed || 0}</p>
                                            </div>
                                            <CheckCircle className="w-8 h-8 text-green-500/30" />
                                        </div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-muted-foreground">Total Earnings</p>
                                                <p className="text-2xl font-bold">${provider?.earnings_total?.toFixed(2) || '0.00'}</p>
                                            </div>
                                            <DollarSign className="w-8 h-8 text-emerald-500/30" />
                                        </div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-muted-foreground">Rating</p>
                                                <p className="text-2xl font-bold flex items-center gap-1">
                                                    <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                                                    {provider?.ratings?.overall?.toFixed(1) || '-'}
                                                </p>
                                            </div>
                                            <span className="text-sm text-muted-foreground">
                                                ({provider?.ratings?.total_ratings || 0} reviews)
                                            </span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Available Jobs Preview */}
                            {jobs.available_jobs.length > 0 && (
                                <Card className="mb-6">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Briefcase className="w-5 h-5 text-teal-500" />
                                            New Job Requests
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-3">
                                            {jobs.available_jobs.slice(0, 3).map((job) => (
                                                <div key={job.id} className="flex items-center justify-between p-3 bg-teal-50 dark:bg-teal-950/20 rounded-xl">
                                                    <div>
                                                        <p className="font-medium capitalize">{job.service_type.replace('_', ' ')}</p>
                                                        <p className="text-sm text-muted-foreground">{job.user_name} • {new Date(job.created_at).toLocaleDateString()}</p>
                                                    </div>
                                                    <Button size="sm" onClick={() => setActiveTab('jobs')}>
                                                        View <ChevronRight className="w-4 h-4 ml-1" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Active Jobs */}
                            {jobs.my_jobs.filter(j => j.status === 'accepted').length > 0 && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Clock className="w-5 h-5 text-amber-500" />
                                            Active Jobs
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-3">
                                            {jobs.my_jobs.filter(j => j.status === 'accepted').map((job) => (
                                                <div key={job.id} className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-950/20 rounded-xl">
                                                    <div>
                                                        <p className="font-medium capitalize">{job.service_type.replace('_', ' ')}</p>
                                                        <p className="text-sm text-muted-foreground">{job.user_name}</p>
                                                    </div>
                                                    <Button 
                                                        size="sm" 
                                                        className="bg-green-500 hover:bg-green-600"
                                                        onClick={() => handleCompleteJob(job.id)}
                                                        disabled={respondingTo === job.id}
                                                    >
                                                        {respondingTo === job.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Complete'}
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </>
                    )}

                    {/* Jobs Tab */}
                    {activeTab === 'jobs' && (
                        <>
                            <div className="mb-6">
                                <h1 className="text-2xl font-bold mb-1">Job Requests</h1>
                                <p className="text-muted-foreground">Manage your service requests</p>
                            </div>

                            {/* Available Jobs */}
                            <Card className="mb-6">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Briefcase className="w-5 h-5 text-teal-500" />
                                        Available Jobs ({jobs.available_jobs.length})
                                    </CardTitle>
                                    <CardDescription>Accept jobs to start earning</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {jobs.available_jobs.length === 0 ? (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                            <p>No available jobs right now</p>
                                            <p className="text-sm">Check back soon!</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {jobs.available_jobs.map((job) => (
                                                <div key={job.id} className="p-4 border rounded-xl bg-white dark:bg-slate-800">
                                                    <div className="flex items-start justify-between mb-3">
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <h3 className="font-semibold capitalize">{job.service_type.replace('_', ' ')}</h3>
                                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getUrgencyBadge(job.urgency)}`}>
                                                                    {job.urgency}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-muted-foreground">{job.description}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                                                        <span className="flex items-center gap-1">
                                                            <User className="w-4 h-4" />
                                                            {job.user_name}
                                                        </span>
                                                        {job.preferred_date && (
                                                            <span className="flex items-center gap-1">
                                                                <Calendar className="w-4 h-4" />
                                                                {job.preferred_date}
                                                            </span>
                                                        )}
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="w-4 h-4" />
                                                            {new Date(job.created_at).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <Button 
                                                            className="flex-1 bg-teal-500 hover:bg-teal-600"
                                                            onClick={() => handleAcceptJob(job.id)}
                                                            disabled={respondingTo === job.id || provider?.folder_status === 'red'}
                                                        >
                                                            {respondingTo === job.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                                                            Accept
                                                        </Button>
                                                        <Button 
                                                            variant="outline"
                                                            onClick={() => handleDeclineJob(job.id)}
                                                            disabled={respondingTo === job.id}
                                                        >
                                                            <XCircle className="w-4 h-4 mr-2" />
                                                            Decline
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* My Jobs */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <CheckCircle className="w-5 h-5 text-green-500" />
                                        My Jobs
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {jobs.my_jobs.length === 0 ? (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <p>No jobs yet</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {jobs.my_jobs.map((job) => (
                                                <div key={job.id} className="flex items-center justify-between p-3 border rounded-lg">
                                                    <div>
                                                        <p className="font-medium capitalize">{job.service_type.replace('_', ' ')}</p>
                                                        <p className="text-sm text-muted-foreground">{job.user_name} • ${job.provider_earnings?.toFixed(2)}</p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                            job.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                                                            job.status === 'accepted' ? 'bg-amber-500/20 text-amber-500' :
                                                            'bg-slate-500/20 text-slate-500'
                                                        }`}>
                                                            {job.status}
                                                        </span>
                                                        {job.status === 'accepted' && (
                                                            <Button 
                                                                size="sm"
                                                                className="bg-green-500 hover:bg-green-600"
                                                                onClick={() => handleCompleteJob(job.id)}
                                                                disabled={respondingTo === job.id}
                                                            >
                                                                Complete
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </>
                    )}

                    {/* Clinical Tools Tab */}
                    {activeTab === 'clinical' && (
                        <>
                            <div className="mb-6">
                                <h1 className="text-2xl font-bold mb-1">Clinical Tools</h1>
                                <p className="text-muted-foreground">AI-powered assessment aids, guidelines, and peer consultation</p>
                            </div>

                            <div className="grid lg:grid-cols-2 gap-6 mb-6">
                                {/* AI Assessment Aid */}
                                <Card className="lg:col-span-2">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Brain className="w-5 h-5 text-purple-500" />
                                            AI Assessment Aid
                                        </CardTitle>
                                        <CardDescription>Enter patient findings to get AI-assisted differential assessment suggestions</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {/* Patient Demographics - Age & Sex */}
                                            <div className="md:col-span-2 grid grid-cols-2 gap-4 p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
                                                <div className="space-y-2">
                                                    <Label className="flex items-center gap-2">
                                                        <User className="w-4 h-4 text-purple-500" />
                                                        Patient Age *
                                                    </Label>
                                                    <Input 
                                                        value={diagnosticInput.patientAge}
                                                        onChange={(e) => setDiagnosticInput({...diagnosticInput, patientAge: e.target.value})}
                                                        placeholder="e.g., 5 years, 3 months, 65 years"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Patient Sex</Label>
                                                    <Select 
                                                        value={diagnosticInput.patientSex || "not_specified"}
                                                        onValueChange={(v) => setDiagnosticInput({...diagnosticInput, patientSex: v === "not_specified" ? "" : v})}
                                                    >
                                                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="not_specified">Not specified</SelectItem>
                                                            <SelectItem value="male">Male</SelectItem>
                                                            <SelectItem value="female">Female</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                            
                                            <div className="space-y-2">
                                                <Label>Patient Symptoms *</Label>
                                                <Textarea 
                                                    value={diagnosticInput.symptoms}
                                                    onChange={(e) => setDiagnosticInput({...diagnosticInput, symptoms: e.target.value})}
                                                    placeholder="Describe presenting symptoms (e.g., chest pain, shortness of breath, fever for 3 days...)"
                                                    rows={3}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Relevant Medical History</Label>
                                                <Textarea 
                                                    value={diagnosticInput.history}
                                                    onChange={(e) => setDiagnosticInput({...diagnosticInput, history: e.target.value})}
                                                    placeholder="Past medical history, medications, allergies..."
                                                    rows={3}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Vital Signs</Label>
                                                <Input 
                                                    value={diagnosticInput.vitals}
                                                    onChange={(e) => setDiagnosticInput({...diagnosticInput, vitals: e.target.value})}
                                                    placeholder="BP, HR, Temp, RR, SpO2..."
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Examination Findings</Label>
                                                <Input 
                                                    value={diagnosticInput.examFindings}
                                                    onChange={(e) => setDiagnosticInput({...diagnosticInput, examFindings: e.target.value})}
                                                    placeholder="Physical exam findings..."
                                                />
                                            </div>
                                        </div>
                                        <div className="flex justify-end">
                                            <Button 
                                                onClick={runDiagnosticAid} 
                                                disabled={clinicalLoading}
                                                className="bg-purple-500 hover:bg-purple-600"
                                            >
                                                {clinicalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                                                Generate Assessment Aid
                                            </Button>
                                        </div>

                                        {diagnosticResult && (
                                            <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-950/30 rounded-xl border border-purple-200 dark:border-purple-800">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h4 className="font-medium text-purple-800 dark:text-purple-200">Assessment Analysis</h4>
                                                    <Button variant="ghost" size="sm" onClick={() => navigator.clipboard.writeText(JSON.stringify(diagnosticResult, null, 2))}>
                                                        <Copy className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                                
                                                {diagnosticResult.differentials && (
                                                    <div className="mb-4">
                                                        <p className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-2">Possible Conditions:</p>
                                                        <div className="space-y-2">
                                                            {diagnosticResult.differentials.map((d, i) => (
                                                                <div key={i} className="flex items-start gap-2 text-sm">
                                                                    <span className="font-bold text-purple-600">{i + 1}.</span>
                                                                    <div>
                                                                        <span className="font-medium">{d.diagnosis}</span>
                                                                        {d.likelihood && <Badge className="ml-2 text-xs">{d.likelihood}</Badge>}
                                                                        {d.reasoning && <p className="text-muted-foreground text-xs mt-1">{d.reasoning}</p>}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {diagnosticResult.recommended_tests && (
                                                    <div className="mb-4">
                                                        <p className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-2">Recommended Investigations:</p>
                                                        <ul className="text-sm space-y-1">
                                                            {diagnosticResult.recommended_tests.map((t, i) => (
                                                                <li key={i} className="flex items-center gap-2">
                                                                    <CheckCircle className="w-3 h-3 text-purple-500" />
                                                                    {t}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {diagnosticResult.red_flags && diagnosticResult.red_flags.length > 0 && (
                                                    <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                                                        <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">⚠️ Red Flags:</p>
                                                        <ul className="text-sm text-red-600 dark:text-red-400">
                                                            {diagnosticResult.red_flags.map((f, i) => (
                                                                <li key={i}>• {f}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {diagnosticResult.age_specific_considerations && (
                                                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg mt-3">
                                                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">👶 Age-Specific Considerations:</p>
                                                        <p className="text-sm text-blue-600 dark:text-blue-400">{diagnosticResult.age_specific_considerations}</p>
                                                    </div>
                                                )}

                                                <p className="text-xs text-muted-foreground mt-4 italic">
                                                    ⚕️ This is an AI-assisted tool for educational purposes only. Always use clinical judgment.
                                                </p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Clinical Guidelines Search */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <BookOpen className="w-5 h-5 text-blue-500" />
                                            Clinical Guidelines
                                        </CardTitle>
                                        <CardDescription>Search NICE, WHO, and other clinical guidelines</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="flex gap-2">
                                            <Input 
                                                value={guidelineSearch}
                                                onChange={(e) => setGuidelineSearch(e.target.value)}
                                                placeholder="Search guidelines (e.g., hypertension, diabetes...)"
                                                onKeyDown={(e) => e.key === 'Enter' && searchGuidelines()}
                                            />
                                            <Button onClick={searchGuidelines} disabled={clinicalLoading}>
                                                <Search className="w-4 h-4" />
                                            </Button>
                                        </div>

                                        {guidelines.length > 0 ? (
                                            <div className="space-y-3 max-h-80 overflow-y-auto">
                                                {guidelines.map((g, i) => (
                                                    <div key={i} className="p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1">
                                                                <p className="font-medium text-sm">{g.title}</p>
                                                                <p className="text-xs text-muted-foreground mt-1">{g.source} • {g.year}</p>
                                                                {g.summary && <p className="text-xs mt-2">{g.summary}</p>}
                                                            </div>
                                                            {g.url && (
                                                                <a href={g.url} target="_blank" rel="noopener noreferrer">
                                                                    <ExternalLink className="w-4 h-4 text-blue-500" />
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-6 text-muted-foreground">
                                                <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                                <p className="text-sm">Search for clinical guidelines</p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Second Opinion Request */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Users className="w-5 h-5 text-teal-500" />
                                            Request Second Opinion
                                        </CardTitle>
                                        <CardDescription>Get peer input on complex cases (patient data anonymized)</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg text-xs text-amber-700 dark:text-amber-300">
                                            <Shield className="w-4 h-4 inline mr-1" />
                                            Patient identifiable information will be automatically redacted
                                        </div>
                                        <div className="space-y-3">
                                            <div className="space-y-2">
                                                <Label>Case Summary (Anonymized)</Label>
                                                <Textarea 
                                                    value={secondOpinionForm.case_summary}
                                                    onChange={(e) => setSecondOpinionForm({...secondOpinionForm, case_summary: e.target.value})}
                                                    placeholder="Describe the case without patient identifiers..."
                                                    rows={3}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Your Question</Label>
                                                <Input 
                                                    value={secondOpinionForm.question}
                                                    onChange={(e) => setSecondOpinionForm({...secondOpinionForm, question: e.target.value})}
                                                    placeholder="What do you need input on?"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Specialty Needed (Optional)</Label>
                                                <Select 
                                                    value={secondOpinionForm.specialty_needed || "any"}
                                                    onValueChange={(v) => setSecondOpinionForm({...secondOpinionForm, specialty_needed: v === "any" ? "" : v})}
                                                >
                                                    <SelectTrigger><SelectValue placeholder="Any specialty" /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="any">Any specialty</SelectItem>
                                                        <SelectItem value="cardiology">Cardiology</SelectItem>
                                                        <SelectItem value="neurology">Neurology</SelectItem>
                                                        <SelectItem value="oncology">Oncology</SelectItem>
                                                        <SelectItem value="pediatrics">Pediatrics</SelectItem>
                                                        <SelectItem value="surgery">Surgery</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        <Button 
                                            onClick={submitSecondOpinion} 
                                            disabled={clinicalLoading}
                                            className="w-full bg-teal-500 hover:bg-teal-600"
                                        >
                                            {clinicalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                            Submit for Peer Review
                                        </Button>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Community Questions */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Users className="w-5 h-5 text-indigo-500" />
                                        Community Questions
                                    </CardTitle>
                                    <CardDescription>Help fellow providers with their clinical questions</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {communityQuestions.length === 0 ? (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                            <p>No community questions yet</p>
                                            <p className="text-sm">Be the first to ask or check back later</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {communityQuestions.map((q) => (
                                                <div key={q.id} className="p-4 border rounded-xl">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div>
                                                            <p className="font-medium">{q.question}</p>
                                                            <p className="text-sm text-muted-foreground mt-1">{q.case_summary?.substring(0, 150)}...</p>
                                                        </div>
                                                        {q.specialty_needed && (
                                                            <Badge variant="outline">{q.specialty_needed}</Badge>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3">
                                                        <span>{q.answers?.length || 0} answers</span>
                                                        <span>{new Date(q.created_at).toLocaleDateString()}</span>
                                                    </div>
                                                    {q.answers?.length > 0 && (
                                                        <div className="mt-3 pt-3 border-t space-y-2">
                                                            {q.answers.slice(0, 2).map((a, i) => (
                                                                <div key={i} className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
                                                                    <p>{a.answer}</p>
                                                                    <p className="text-xs text-muted-foreground mt-1">— {a.provider_name}</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </>
                    )}

                    {/* Earnings Tab */}
                    {activeTab === 'earnings' && (
                        <>
                            <div className="mb-6">
                                <h1 className="text-2xl font-bold mb-1">Earnings & Subscription</h1>
                                <p className="text-muted-foreground">Track your earnings and manage your subscription</p>
                            </div>

                            {/* Trial/Subscription Status Banner */}
                            {provider?.subscription?.status === 'trial' && (
                                <Card className="mb-6 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
                                    <CardContent className="p-4">
                                        <div className="flex items-start gap-3">
                                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                                            <div className="flex-1">
                                                <p className="font-medium text-amber-800 dark:text-amber-200">Trial Period</p>
                                                <p className="text-sm text-amber-700 dark:text-amber-300">
                                                    You have {provider?.subscription?.trial_jobs_remaining || 0} trial jobs remaining at 20% commission.
                                                    Choose a subscription plan to unlock lower commission rates.
                                                </p>
                                            </div>
                                            <Button 
                                                size="sm" 
                                                className="bg-amber-600 hover:bg-amber-700"
                                                onClick={() => fetchSubscriptionPlans()}
                                            >
                                                View Plans
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Earnings Summary */}
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-muted-foreground">Total Earnings</p>
                                                <p className="text-2xl font-bold text-emerald-600">${earnings?.total_earnings?.toFixed(2) || '0.00'}</p>
                                            </div>
                                            <DollarSign className="w-8 h-8 text-emerald-500/30" />
                                        </div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-muted-foreground">This Week</p>
                                                <p className="text-2xl font-bold">${earnings?.earnings_this_week?.toFixed(2) || '0.00'}</p>
                                            </div>
                                            <TrendingUp className="w-8 h-8 text-blue-500/30" />
                                        </div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-muted-foreground">This Month</p>
                                                <p className="text-2xl font-bold">${earnings?.earnings_this_month?.toFixed(2) || '0.00'}</p>
                                            </div>
                                            <Calendar className="w-8 h-8 text-purple-500/30" />
                                        </div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-muted-foreground">Pending Payout</p>
                                                <p className="text-2xl font-bold text-amber-600">${earnings?.pending_earnings?.toFixed(2) || '0.00'}</p>
                                            </div>
                                            <Wallet className="w-8 h-8 text-amber-500/30" />
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            <div className="grid lg:grid-cols-2 gap-6">
                                {/* Payout Settings */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Settings className="w-5 h-5 text-teal-500" />
                                            Payout Settings
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div>
                                            <p className="text-sm text-muted-foreground mb-2">Payout Schedule</p>
                                            <Select 
                                                value={earnings?.payout_schedule || 'weekly'} 
                                                onValueChange={updatePayoutSchedule}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="weekly">Weekly</SelectItem>
                                                    <SelectItem value="fortnightly">Fortnightly (Every 2 weeks)</SelectItem>
                                                    <SelectItem value="monthly">Monthly</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                            <p className="text-xs text-muted-foreground">Current Commission Rate</p>
                                            <p className="font-medium text-lg">{earnings?.commission_rate || 20}%</p>
                                        </div>
                                        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                            <p className="text-xs text-muted-foreground">Current Plan</p>
                                            <p className="font-medium capitalize">{provider?.subscription?.plan || 'Trial'}</p>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Subscription Plans */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <CreditCard className="w-5 h-5 text-teal-500" />
                                            Subscription Plans
                                        </CardTitle>
                                        <CardDescription>Upgrade to reduce your commission rate</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        {subscriptionPlans.length === 0 ? (
                                            <Button variant="outline" onClick={fetchSubscriptionPlans} className="w-full">
                                                Load Subscription Plans
                                            </Button>
                                        ) : (
                                            subscriptionPlans.map((plan) => (
                                                <div key={plan.id} className={`p-3 rounded-lg border-2 ${
                                                    provider?.subscription?.plan === plan.id 
                                                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30' 
                                                        : 'border-slate-200 dark:border-slate-700'
                                                }`}>
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <p className="font-medium">${plan.price}/month</p>
                                                            <p className="text-sm text-muted-foreground">
                                                                {plan.commission === 0 ? 'No commission' : `${plan.commission}% commission`}
                                                            </p>
                                                        </div>
                                                        {provider?.subscription?.plan === plan.id ? (
                                                            <Badge className="bg-teal-500">Current</Badge>
                                                        ) : (
                                                            <Button 
                                                                size="sm" 
                                                                onClick={() => handleSubscribe(plan.id)}
                                                                disabled={subscribing}
                                                            >
                                                                {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Select'}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Earnings History */}
                            <Card className="mt-6">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Receipt className="w-5 h-5 text-teal-500" />
                                        Recent Earnings
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {!earnings?.earnings_history?.length ? (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                            <p>No earnings yet</p>
                                            <p className="text-sm">Complete jobs to start earning</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {earnings.earnings_history.map((item, idx) => (
                                                <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                                            <DollarSign className="w-5 h-5 text-emerald-600" />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium capitalize">{item.service_type?.replace('_', ' ')}</p>
                                                            <p className="text-sm text-muted-foreground">{item.user_name} • {new Date(item.completed_at).toLocaleDateString()}</p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="font-bold text-emerald-600">+${item.earnings?.toFixed(2)}</p>
                                                        <p className="text-xs text-muted-foreground">Fee: ${item.platform_fee?.toFixed(2)}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </>
                    )}

                    {/* Profile Tab */}
                    {activeTab === 'profile' && (
                        <>
                            <div className="mb-6">
                                <h1 className="text-2xl font-bold mb-1">My Profile</h1>
                                <p className="text-muted-foreground">View and manage your profile</p>
                            </div>

                            <div className="grid lg:grid-cols-2 gap-6">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Personal Information</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white text-2xl font-bold">
                                                {provider?.name?.charAt(0)}
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-lg">{provider?.name}</h3>
                                                <p className="text-muted-foreground capitalize">{provider?.profession}</p>
                                            </div>
                                        </div>
                                        <div className="grid gap-3">
                                            <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                                <Mail className="w-4 h-4 text-muted-foreground" />
                                                <span>{provider?.email}</span>
                                            </div>
                                            <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                                <Phone className="w-4 h-4 text-muted-foreground" />
                                                <span>{provider?.phone || 'Not provided'}</span>
                                            </div>
                                            <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                                <MapPin className="w-4 h-4 text-muted-foreground" />
                                                <span>Travel Distance: {provider?.travel_distance_km} km</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader>
                                        <CardTitle>Professional Details</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid gap-3">
                                            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                                <p className="text-xs text-muted-foreground">Registration Number</p>
                                                <p className="font-medium">{provider?.registration_number}</p>
                                            </div>
                                            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                                <p className="text-xs text-muted-foreground">Regulatory Body</p>
                                                <p className="font-medium">{provider?.regulatory_body}</p>
                                            </div>
                                            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                                <p className="text-xs text-muted-foreground">Specialty</p>
                                                <p className="font-medium">{provider?.specialty || 'General'}</p>
                                            </div>
                                            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                                <p className="text-xs text-muted-foreground">Subscription</p>
                                                <p className="font-medium">{provider?.subscription?.plan?.replace('_', ' ')} ({provider?.subscription?.commission_rate}% commission)</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Ratings Breakdown */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Star className="w-5 h-5 text-amber-500" />
                                            Ratings
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-center mb-4">
                                            <p className="text-4xl font-bold flex items-center justify-center gap-2">
                                                <Star className="w-8 h-8 fill-amber-400 text-amber-400" />
                                                {provider?.ratings?.overall?.toFixed(1) || '-'}
                                            </p>
                                            <p className="text-sm text-muted-foreground">{provider?.ratings?.total_ratings || 0} reviews</p>
                                        </div>
                                        {provider?.ratings?.breakdown && (
                                            <div className="space-y-2">
                                                {Object.entries(provider.ratings.breakdown).map(([key, value]) => (
                                                    <div key={key} className="flex items-center justify-between">
                                                        <span className="text-sm capitalize">{key}</span>
                                                        <div className="flex items-center gap-1">
                                                            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                                                            <span>{value?.toFixed(1) || '-'}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Documents Status */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <FileText className="w-5 h-5 text-teal-500" />
                                            Documents
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-3">
                                            {['government_id', 'registration_certificate'].map((docType) => {
                                                const doc = provider?.documents?.[docType];
                                                return (
                                                    <div key={docType} className="flex items-center justify-between p-3 border rounded-lg">
                                                        <div>
                                                            <p className="font-medium capitalize">{docType.replace('_', ' ')}</p>
                                                            {doc?.expiry_date && (
                                                                <p className="text-xs text-muted-foreground">
                                                                    Expires: {new Date(doc.expiry_date).toLocaleDateString()}
                                                                </p>
                                                            )}
                                                        </div>
                                                        {doc?.verified ? (
                                                            <CheckCircle className="w-5 h-5 text-green-500" />
                                                        ) : (
                                                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </>
                    )}

                    {/* Notifications Tab */}
                    {activeTab === 'notifications' && (
                        <>
                            <div className="mb-6">
                                <h1 className="text-2xl font-bold mb-1">Notifications</h1>
                                <p className="text-muted-foreground">Your recent notifications</p>
                            </div>

                            <Card>
                                <CardContent className="p-4">
                                    {notifications.length === 0 ? (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                            <p>No notifications yet</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {notifications.map((notif) => (
                                                <div key={notif.id} className={`p-4 rounded-lg border ${notif.read ? 'bg-white dark:bg-slate-800' : 'bg-teal-50 dark:bg-teal-950/20 border-teal-200'}`}>
                                                    <div className="flex items-start gap-3">
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                                            notif.notification_type === 'job_request' ? 'bg-blue-100 text-blue-500' :
                                                            notif.notification_type === 'success' ? 'bg-green-100 text-green-500' :
                                                            notif.notification_type === 'warning' ? 'bg-amber-100 text-amber-500' :
                                                            'bg-slate-100 text-slate-500'
                                                        }`}>
                                                            <Bell className="w-4 h-4" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="font-medium">{notif.title}</p>
                                                            <p className="text-sm text-muted-foreground">{notif.message}</p>
                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                {new Date(notif.created_at).toLocaleString()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </>
                    )}
                </main>
            </div>

            {/* Mobile overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}
        </div>
    );
}
